import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import {
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Table,
  TableWrap,
  Td,
  Th,
  formatDate,
} from "./ui";

interface ApiToken {
  id: string;
  name: string;
  prefix: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  user: { id: string; email: string; role: string };
}

/**
 * Machine access, for scripts and agents that cannot sign in.
 *
 * Owner-only, and worth reading the warning on the card: a token acts as an
 * ADMIN even when an owner mints it, so it can edit the catalogue and issue
 * licences but can never delete one, manage staff, or mint another token.
 * That ceiling is what makes a long-lived credential safe to hand out — a
 * leaked token cannot widen its own reach or erase the trail behind it.
 */
export function ApiTokens() {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [minted, setMinted] = useState<{ name: string; token: string } | null>(null);

  const tokens = useQuery({
    queryKey: ["api-tokens"],
    queryFn: () => api<ApiToken[]>("/api/v1/admin/api-tokens"),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ["api-tokens"] });

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<{ id: string; name: string; token: string }>("/api/v1/admin/api-tokens", {
        method: "POST",
        body,
      }),
    onSuccess: (result) => {
      setMinted({ name: result.name, token: result.token });
      setAdding(false);
      invalidate();
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api(`/api/v1/admin/api-tokens/${id}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-4">
      {minted !== null && (
        <Card title="Token created">
          <div className="space-y-4 p-4">
            <p className="text-sm text-muted">
              Copy it now — only its hash is stored, so this is the one and only time it can be
              shown. Send it over something private, and revoke it here the moment it is no longer
              needed.
            </p>
            <code className="block break-all rounded border border-accent/40 bg-base px-3 py-3 tabular text-sm text-ink">
              {minted.token}
            </code>
            <div className="flex gap-2">
              <Button
                variant="primary"
                onClick={() => void navigator.clipboard.writeText(minted.token)}
              >
                Copy token
              </Button>
              <Button onClick={() => setMinted(null)}>I have saved it</Button>
            </div>
          </div>
        </Card>
      )}

      <Card
        title="API tokens"
        action={
          <Button variant="primary" onClick={() => setAdding((value) => !value)}>
            {adding ? "Cancel" : "New token"}
          </Button>
        }
      >
        <p className="border-b border-hairline px-4 pb-4 pt-1 text-sm text-muted">
          For scripts and agents. A token acts as an <strong className="text-ink">admin</strong>{" "}
          whoever mints it: it can read and edit the catalogue, issue licences and raise orders,
          and it cannot delete a licence, manage staff accounts, or create another token. Those
          stay with a signed-in owner.
        </p>

        {adding && (
          <TokenForm
            pending={create.isPending}
            error={create.isError ? (create.error as Error).message : null}
            onSubmit={(body) => create.mutate(body)}
            onCancel={() => setAdding(false)}
          />
        )}

        {revoke.isError && <ErrorNote>{(revoke.error as Error).message}</ErrorNote>}

        {tokens.isPending ? (
          <EmptyState>Loading…</EmptyState>
        ) : (tokens.data?.length ?? 0) === 0 ? (
          <EmptyState>No tokens. Nothing outside a browser can reach the API.</EmptyState>
        ) : (
          <TableWrap>
            <Table>
              <thead className="border-b border-hairline">
                <tr>
                  <Th>Name</Th>
                  <Th>Token</Th>
                  <Th>Acts as</Th>
                  <Th>Last used</Th>
                  <Th>Expires</Th>
                  <Th className="text-right">Revoke</Th>
                </tr>
              </thead>
              <tbody>
                {(tokens.data ?? []).map((token) => (
                  <tr key={token.id} className="border-b border-hairline last:border-0">
                    <Td label="Name">{token.name}</Td>
                    <Td label="Token" className="tabular text-muted">
                      {token.prefix}…
                    </Td>
                    <Td label="Acts as" className="text-muted">
                      <span className="block">{token.user.email}</span>
                      <span className="block text-xs text-dim">as admin</span>
                    </Td>
                    <Td label="Last used" className="text-muted">
                      {token.lastUsedAt === null ? "Never" : formatDate(token.lastUsedAt)}
                    </Td>
                    <Td label="Expires" className="text-muted">
                      {token.expiresAt === null ? "Never" : formatDate(token.expiresAt)}
                    </Td>
                    <Td label="Revoke" className="text-right">
                      <Button
                        variant="ghost"
                        disabled={revoke.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              `Revoke "${token.name}"? Anything using it stops working immediately.`,
                            )
                          ) {
                            revoke.mutate(token.id);
                          }
                        }}
                      >
                        Revoke
                      </Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>
    </div>
  );
}

function TokenForm({
  pending,
  error,
  onSubmit,
  onCancel,
}: {
  pending: boolean;
  error: string | null;
  onSubmit: (body: Record<string, unknown>) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [days, setDays] = useState("");

  return (
    <form
      className="space-y-4 border-b border-hairline p-4"
      onSubmit={(event) => {
        event.preventDefault();
        const expiresInDays = days.trim() === "" ? undefined : Number(days);
        onSubmit({
          name,
          ...(expiresInDays === undefined ? {} : { expiresInDays }),
        });
      }}
    >
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="What is it for" hint="So the right one can be revoked later.">
          <Input
            value={name}
            required
            placeholder="e.g. deployment script"
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field label="Expires in days" hint="Leave empty for a token that never expires.">
          <Input
            type="number"
            min={1}
            max={3650}
            value={days}
            placeholder="e.g. 90"
            onChange={(event) => setDays(event.target.value)}
          />
        </Field>
      </div>

      {error !== null && <ErrorNote>{error}</ErrorNote>}

      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={pending}>
          {pending ? "Creating…" : "Create token"}
        </Button>
        <Button type="button" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
